// QR Kiosk v2 — §8 fullscreen MIDAS takeover (overlay, NOT a route). Four stages:
// chat / draft (ticket) / history (§8.3) / artifact (§8.4). Round-6: the takeover is
// PORTALED into the kiosk root (escapes the dashboard's animated wrappers), sized
// `inset: 0` with `paddingBottom: keyboardInset` (useKeyboardInset) so the composer
// glues to the keyboard's top edge and the opaque root always covers the full layout
// viewport (no dashboard bleed-through). It opens on the WELCOME screen — the keyboard
// appears only when the user taps the composer input (the §8.1 autofocus was REMOVED,
// owner reversal). Machine-scoped artifact resolution via MidasSurfaceContext (§5b);
// session survives close/reopen through Redux. UUID re-guard (dock gates too). §3-T3 + §8.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import { Machine } from "../../models/machinesModels";
import { isValidMachineUuid } from "../dashboard/kioskLiveStatus";
import { isGuestRole } from "../guest/guestSession";
import { Glyph, GD, Spark } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { useKioskOverlayRoot } from "../shared/kioskOverlayRoot";
import { useKeyboardInset } from "../shared/useKeyboardInset";
import { useKioskMidasSession } from "./useKioskMidasSession";
import { MidasSurfaceContext, KioskMidasSurface } from "./midasSurfaceContext";
import { resolveKioskArtifactImageUrl, resolveKioskArtifactPage } from "./kioskArtifactImage";
import { MidasMessages } from "./MidasMessages";
import { MidasComposer } from "./MidasComposer";
import { MidasWelcome } from "./MidasWelcome";
import { DraftTicketArtifactV2 } from "./DraftTicketArtifactV2";
import { MidasHistoryTakeoverV2 } from "./MidasHistoryTakeoverV2";
import { MidasArtifactViewV2 } from "./MidasArtifactViewV2";

type MidasStage = "chat" | "draft" | "history" | "artifact";

const accentStyle = {
  "--accent": "var(--midas)",
  "--accent-hov": "var(--midas-hov)",
  "--accent-soft": "var(--midas-soft)",
  "--accent-line": "var(--midas-line)",
} as React.CSSProperties;

export interface KioskMidasFullV2Props {
  machine: Machine;
  initialSend?: string;
  onClose: () => void;
}

export function KioskMidasFullV2({ machine, initialSend, onClose }: KioskMidasFullV2Props) {
  const { translate } = useTranslation("public");
  const { isPhone, isTablet } = useBreakpoint();
  const role = useAppSelector((s) => s.account.user?.role);
  const overlayRoot = useKioskOverlayRoot();
  const { keyboardInset } = useKeyboardInset();
  const sess = useKioskMidasSession(machine);
  const [stage, setStage] = useState<MidasStage>("chat");
  const [draftMessageId, setDraftMessageId] = useState<string | null>(null);
  const [artifactView, setArtifactView] = useState<{ url: string; title: string } | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const backPress = usePressed();
  const menuPress = usePressed();
  const initialSentRef = useRef(false);

  // Body-scroll lock while the takeover is open (v1 parity).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Auto-scroll on message / streaming change; re-pin when the keyboard opens (inset).
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [sess.messages.length, sess.isLoading, sess.streamingText, keyboardInset]);

  // initialSend — consume ONCE on mount (the dock chip handoff, T6).
  useEffect(() => {
    if (initialSentRef.current) return;
    if (initialSend && initialSend.trim() && !sess.isLoading) {
      initialSentRef.current = true;
      sess.send(initialSend);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSend]);

  // §5b — page/document artifact resolution over the session's messages. URL
  // resolution is delegated to resolveKioskArtifactImageUrl (shared with the
  // MidasPageArtifactCard thumbnails) so schema/diagram pages map to the
  // `/v1/bot/diagram/{page}/image` endpoint the same way desktop does.
  const artifactMap = useMemo(() => {
    const m = new Map<number, { url: string; title: string }>();
    for (const msg of sess.messages) {
      for (const a of msg.artifacts || []) {
        if (a.source !== "cross_reference" && a.type !== "diagram_overlay") continue;
        const page = resolveKioskArtifactPage(a);
        const url = resolveKioskArtifactImageUrl(a);
        if (page != null && url && !m.has(page)) m.set(page, { url, title: a.title || "" });
      }
    }
    return m;
  }, [sess.messages]);

  const surface = useMemo<KioskMidasSurface>(
    () => ({
      resolvePage: (page) => artifactMap.has(page),
      openPageArtifact: (page) => {
        const hit = artifactMap.get(page);
        if (!hit) return;
        setArtifactView({ url: hit.url, title: hit.title || translate("kioskV2MidasArtifactPage", { page }) });
        setStage("artifact");
      },
      openImageArtifact: (url, title) => {
        setArtifactView({ url, title: title || translate("kioskV2MidasArtifactTitle") });
        setStage("artifact");
      },
    }),
    [artifactMap, translate]
  );

  // Interim guest-guard (double enforcement; the dock hides the only launcher). The kiosk
  // MIDAS bot proxy is company-role-gated → a guest session would 403 into a dead assistant.
  // Never mount the takeover for a guest until the deferred per-company-toggle guest-MIDAS ships.
  if (isGuestRole(role)) return null;
  if (!isValidMachineUuid(machine.id)) return null;
  if (!overlayRoot) return null; // portal target not mounted yet (never in practice)

  const columnMaxWidth: number | string = isPhone ? "100%" : isTablet ? 560 : 640;
  const isEmpty = sess.messages.length === 0 && !sess.isLoading;
  const serialSuffix = machine.serialNumber ? ` · ${translate("serialNumber")} ${machine.serialNumber}` : "";

  const openDraft = (messageId: string) => {
    setDraftMessageId(messageId);
    setStage("draft");
  };

  return createPortal(
    <MidasSurfaceContext.Provider value={surface}>
      {/* kv2-fade (opacity-only): this root holds fixed descendants (stage BackPills z42,
          artifact zoom z43, MidasImageViewer 1400) — a transform entrance would make it a
          containing block and re-break their `fixed`. inset:0 + paddingBottom keyboardInset
          keeps the opaque root over the full layout viewport and the composer above the keyboard. */}
      <div
        className="kv2-fade"
        style={{
          position: "fixed",
          inset: 0,
          paddingBottom: keyboardInset,
          zIndex: 1300,
          background: "var(--bg)",
          display: "flex",
          flexDirection: "column",
          ...accentStyle,
        }}
      >
        <div style={{ width: "100%", maxWidth: columnMaxWidth, margin: "0 auto", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {stage === "draft" && draftMessageId ? (
            <DraftTicketArtifactV2
              stage
              machine={machine}
              draft={sess.drafts[draftMessageId]}
              messageId={draftMessageId}
              created={!!sess.createdDrafts[draftMessageId]}
              onBack={() => setStage("chat")}
            />
          ) : stage === "history" ? (
            <MidasHistoryTakeoverV2 machine={machine} currentThreadId={sess.threadId} onDone={() => setStage("chat")} />
          ) : stage === "artifact" && artifactView ? (
            <MidasArtifactViewV2 machine={machine} view={artifactView} onBack={() => setStage("chat")} />
          ) : (
            <>
              {/* Top bar (designer MidasTopBar) — back (close) · title · hamburger. */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, height: 54, padding: "0 12px", background: "var(--card)", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
                <button
                  type="button"
                  aria-label={translate("kioskMidasClose")}
                  {...backPress.pressHandlers}
                  onClick={onClose}
                  style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid var(--border)", background: "var(--card)", display: "grid", placeItems: "center", color: "var(--ink-2)", cursor: "pointer", flexShrink: 0, ...backPress.pressStyle }}
                >
                  <Glyph d={GD.chev} size={18} style={{ transform: "rotate(180deg)" }} />
                </button>
                <span style={{ width: 28, height: 28, borderRadius: 8, background: "var(--midas-soft)", display: "inline-grid", placeItems: "center", color: "var(--midas)" }}>
                  <Spark size={14} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>MIDAS</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--faint)", letterSpacing: "0.05em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {`${machine.model}${serialSuffix}`}
                  </div>
                </div>
                <button
                  type="button"
                  aria-label={translate("kioskV2MidasHistoryTitle")}
                  {...menuPress.pressHandlers}
                  onClick={() => setStage("history")}
                  style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid var(--border)", background: "var(--card)", display: "grid", placeItems: "center", color: "var(--ink-2)", cursor: "pointer", flexShrink: 0, ...menuPress.pressStyle }}
                >
                  <Glyph d={GD.menu} size={20} />
                </button>
              </div>

              {/* Stream region. */}
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", padding: "14px 16px" }}>
                {isEmpty ? (
                  <MidasWelcome onSendPrompt={sess.send} />
                ) : (
                  <MidasMessages
                    messages={sess.messages}
                    streamingText={sess.streamingText}
                    isLoading={sess.isLoading}
                    currentStep={sess.currentStep}
                    error={sess.error}
                    drafts={sess.drafts}
                    createdDrafts={sess.createdDrafts}
                    machine={machine}
                    onOpenArtifact={openDraft}
                  />
                )}
                <div ref={bottomRef} />
              </div>

              {/* Composer — no autofocus (Round-6): the keyboard opens only when the user
                  taps the input, so the takeover shows the welcome screen keyboard-free. */}
              <MidasComposer
                isLoading={sess.isLoading}
                onSend={sess.send}
                onStop={sess.stop}
              />
            </>
          )}
        </div>
      </div>
    </MidasSurfaceContext.Provider>,
    overlayRoot
  );
}

export default KioskMidasFullV2;
